/**
 * Das Gedächtnis der Team-Wahl im TeamDaily.
 *
 * Bereich und Tag stehen in der Adresse, das gewählte Team bewusst nicht: Wer
 * sein Daily-Team einstellt, will es morgen wiederhaben, aber er will es nicht
 * mitverschicken, wenn er jemandem den Link zu einem bestimmten Tag schickt.
 * Ein geteilter Link zeigt deshalb weiter den ganzen Bereich, der eigene
 * Browser dagegen merkt sich die Wahl **je Bereich**.
 *
 * Gemerkt wird seit dem 2026-10-01 (#264) die **Kennung des Teams**, vorher
 * war es eine private Personenliste. Das Team selbst ist jetzt serverseitig
 * da und gilt für alle; der Browser merkt sich nur noch, welches davon zuletzt
 * offen war.
 *
 * Die Funktionen hier nehmen den rohen Speicherinhalt als Parameter und sind
 * damit ohne Browser prüfbar; `teamLaden`/`teamSpeichern` fassen `localStorage`
 * an und schlucken dessen Fehler (privates Fenster, abgeschaltete Speicherung).
 * **Fail-soft ist hier Pflicht:** Ein kaputter Eintrag darf höchstens die Wahl
 * kosten, niemals das Board.
 */

export const TEAM_SPEICHER_SCHLUESSEL = "msc-teamdaily-team";

/**
 * Gemerktes Team eines Bereichs aus dem rohen Speicherinhalt.
 *
 * Verträgt den **Altbestand**: Bis zum 2026-10-01 stand dort eine Liste von
 * Personen-Kennungen. Die ist jetzt bedeutungslos und wird wie „nichts
 * gemerkt" behandelt, statt sie als Team-Kennung misszuverstehen.
 */
export function gemerktesTeam(roh: string | null, segmentId: string): string | null {
  if (!roh) return null;
  let gelesen: unknown;
  try {
    gelesen = JSON.parse(roh);
  } catch {
    return null;
  }
  if (typeof gelesen !== "object" || gelesen === null || Array.isArray(gelesen)) {
    return null;
  }
  const eintrag = (gelesen as Record<string, unknown>)[segmentId];
  return typeof eintrag === "string" && eintrag.length > 0 ? eintrag : null;
}

/**
 * Rohen Speicherinhalt mit der Wahl eines Bereichs — die anderen Bereiche
 * bleiben stehen. `null` **löscht** den Eintrag: „kein Team gewählt" ist der
 * Ausgangszustand (ganzer Bereich) und kein gemerkter Wunsch.
 */
export function teamGemerkt(
  roh: string | null,
  segmentId: string,
  teamId: string | null,
): string {
  let bestand: Record<string, unknown> = {};
  if (roh) {
    try {
      const gelesen: unknown = JSON.parse(roh);
      if (typeof gelesen === "object" && gelesen !== null && !Array.isArray(gelesen)) {
        bestand = gelesen as Record<string, unknown>;
      }
    } catch {
      bestand = {};
    }
  }
  if (teamId === null) {
    delete bestand[segmentId];
  } else {
    bestand[segmentId] = teamId;
  }
  return JSON.stringify(bestand);
}

/**
 * Das gemerkte Team, sofern es den Bereich noch gibt und das Team noch
 * existiert. Sonst `null`.
 *
 * Ohne diese Prüfung bliebe eine Kennung liegen, wenn jemand das Team löscht,
 * und das Board antwortete dauerhaft mit `TEAM_NOT_FOUND` — ein Fehler, den
 * niemand wegklicken kann, ohne zu wissen, dass die Ursache im eigenen Browser
 * liegt.
 */
export function teamNochDa(
  gemerkt: string | null,
  vorhanden: readonly string[],
): string | null {
  if (!gemerkt) return null;
  return vorhanden.includes(gemerkt) ? gemerkt : null;
}

/**
 * Welches Team beim Öffnen gelten soll: das gemerkte, sonst das eigene, wenn
 * es genau eines gibt. Wer in genau einem Team seines Bereichs steht, soll
 * morgens nichts auswählen müssen; wer in mehreren steht, wird nicht geraten.
 */
export function startTeam(
  gemerkt: string | null,
  vorhanden: readonly string[],
  eigene: readonly string[],
): string | null {
  const bestand = teamNochDa(gemerkt, vorhanden);
  if (bestand) return bestand;
  const eigenes = eigene.filter((id) => vorhanden.includes(id));
  return eigenes.length === 1 ? eigenes[0]! : null;
}

/** Gemerktes Team eines Bereichs; bei jedem Speicherproblem `null`. */
export function teamLaden(segmentId: string): string | null {
  try {
    return gemerktesTeam(localStorage.getItem(TEAM_SPEICHER_SCHLUESSEL), segmentId);
  } catch {
    return null;
  }
}

/** Wahl eines Bereichs merken; Speicherprobleme bleiben folgenlos. */
export function teamSpeichern(segmentId: string, teamId: string | null): void {
  try {
    localStorage.setItem(
      TEAM_SPEICHER_SCHLUESSEL,
      teamGemerkt(localStorage.getItem(TEAM_SPEICHER_SCHLUESSEL), segmentId, teamId),
    );
  } catch {
    // Kein Speicher, kein Gedächtnis — das Board funktioniert trotzdem.
  }
}
